import { ApiError, type ApiClient, type ChatConversation } from "./api";
import { useLoaded } from "./useLoaded";

export interface ConversationsState {
  conversations: ChatConversation[];
  loading: boolean;
  problem: string | null;
  /**
   * Whether this deployment has a chat at all.
   *
   * The Server maps the chat routes only where an MCP server is configured, so a 404 here is
   * an answer rather than a failure: there is nothing to reach a corpus with, and a chat that
   * cannot reach its sources would be a chat that answers ungrounded. False turns the page
   * into an honest "not available here" rather than an error somebody would try to fix.
   */
  available: boolean;
  refresh: () => void;
}

interface Conversations {
  conversations: ChatConversation[];
  available: boolean;
}

async function readConversations(api: ApiClient): Promise<Conversations> {
  try {
    return { conversations: await api.getConversations(), available: true };
  } catch (failure) {
    if (failure instanceof ApiError && failure.status === 404) return { conversations: [], available: false };
    throw failure;
  }
}

/**
 * CHAT-2 — the member's threads, held once.
 *
 * In the shared context for the reason the recordings list is: the navigator panel lists
 * conversations and the page opens one, and a panel reading its own copy is how the two come
 * to disagree about which threads exist a second after one is deleted.
 *
 * Refreshed on demand rather than polled. A conversation changes only when the person in
 * front of it asks something, starts one or deletes one — there is no background process that
 * can add one.
 */
export function useConversations(api: ApiClient): ConversationsState {
  const { value, loading, problem, refresh } = useLoaded(
    api,
    readConversations,
    { conversations: [], available: true },
    "Your conversations could not be read.",
  );

  return { ...value, loading, problem, refresh };
}
